import moment from "moment";
import type {
  AuditDetailModel,
  AuditDetailItem,
  StoreReconciliationAuditShiftModel,
} from "models";
import { getAuditDetails } from "services/auditService";
import { getGamePackReport } from "services/scratcherService";

interface AuditPackSnapshot {
  auditId: string;
  gameId: string;
  gameName: string;
  packId: string;
  ticketPrice?: number;
  currentPackStatus?: string;
  endingTicket: number | null;
  endingTicketDisplay?: string;
  qtySold: number;
  posQty: number;
  quantityVariance: number;
  isManualSoldOut: boolean;
}

type AuditShiftReference = Pick<
  StoreReconciliationAuditShiftModel,
  "auditId" | "reportDatetime"
> &
  Partial<
    Pick<StoreReconciliationAuditShiftModel, "userName" | "operatorName">
  >;

interface AuditedGamePackCsvOptions {
  storeId: string;
  storeName: string | null;
  startDate: string;
  endDate: string;
  shifts: AuditShiftReference[];
}

export interface AuditedGamePackCsvFile {
  blob: Blob;
  filename: string;
}

type AuditTicketValue = number | string;

interface AuditColumn {
  auditId: string;
  auditDatetime: string;
  userName?: string | null;
  operatorName?: string | null;
}

interface AuditedGamePackRow {
  gameName: string;
  gameId: string;
  packId: string;
  ticketPrice: number | "";
  currentPackStatus: string;
  totalExpected: number;
  ticketsByAudit: Map<string, AuditTicketValue>;
}

interface AuditedGameRangeTotals {
  gameId: string;
  reportedPos: number;
  variance: number;
}

interface LoadedAuditDetail {
  shift: AuditShiftReference;
  detail: AuditDetailModel;
}

const AUDIT_DETAIL_REQUEST_CONCURRENCY = 4;
const UNKNOWN_PACK_STATUS = "---";

const normalizeNumericId = (value: unknown): string => {
  const withoutAnnotation = String(value ?? "").trim().replace(/\s*\([^)]*\)$/, "");
  if (!/^\d+$/.test(withoutAnnotation)) {
    return withoutAnnotation;
  }

  return withoutAnnotation.replace(/^0+(?=\d)/, "");
};

const packKey = (gameId: unknown, packId: unknown) =>
  `${normalizeNumericId(gameId)}:${normalizeNumericId(packId)}`;

const compareNumericText = (left: string, right: string): number => {
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftNumber !== rightNumber) {
    return leftNumber - rightNumber;
  }

  return left.localeCompare(right, undefined, { numeric: true });
};

const csvCell = (value: unknown): string => {
  const text = value == null ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const formatAuditDate = (value: string): string => {
  const parsed = moment.parseZone(value);
  return parsed.isValid() ? parsed.format("MM/DD hh:mm A") : value;
};

const getAuditUserLabel = (audit: AuditColumn): string =>
  audit.userName || audit.operatorName || "";

const getAuditTicketValue = (
  snapshot: AuditPackSnapshot
): AuditTicketValue | null => {
  const displayValue = snapshot.endingTicketDisplay?.trim();
  if (displayValue && displayValue.toUpperCase() !== "END") {
    return /^\d+$/.test(displayValue)
      ? Number(displayValue) % 1000
      : displayValue;
  }

  const isEnded =
    snapshot.endingTicket == null ||
    snapshot.endingTicket === -1 ||
    displayValue?.toUpperCase() === "END";

  if (isEnded) {
    return snapshot.qtySold > 0 || snapshot.isManualSoldOut ? "END" : null;
  }

  return Math.abs(Number(snapshot.endingTicket)) % 1000;
};

const loadAuditDetails = async (
  storeId: string,
  shifts: AuditShiftReference[]
): Promise<LoadedAuditDetail[]> => {
  const results = new Array<LoadedAuditDetail>(shifts.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < shifts.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      const shift = shifts[currentIndex];
      const detail = await getAuditDetails(storeId, shift.auditId);
      if (!detail) {
        throw new Error(`Unable to load audit ${shift.auditId}.`);
      }
      results[currentIndex] = { shift, detail };
    }
  };

  await Promise.all(
    Array.from(
      {
        length: Math.min(AUDIT_DETAIL_REQUEST_CONCURRENCY, shifts.length),
      },
      () => worker()
    )
  );
  return results;
};

const loadCurrentPackStatuses = async (
  storeId: string
): Promise<Map<string, string>> => {
  const response = await getGamePackReport(storeId);
  if (!response.success || !response.data) {
    return new Map();
  }

  const statuses = new Map<string, string>();
  const games = Array.isArray(response.data.games) ? response.data.games : [];
  games.forEach((game: any) => {
    const packs = Array.isArray(game?.packs) ? game.packs : [];
    packs.forEach((pack: any) => {
      const status = String(pack?.status ?? "").trim();
      statuses.set(
        packKey(game?.game_id, pack?.pack_id),
        status || UNKNOWN_PACK_STATUS
      );
    });
  });
  return statuses;
};

export const buildAuditedGamePacksCsv = (
  snapshots: AuditPackSnapshot[],
  auditColumns: AuditColumn[]
): string => {
  const rowsByPack = new Map<string, AuditedGamePackRow>();
  const totalsByAuditGame = new Map<string, AuditedGameRangeTotals>();

  snapshots.forEach((snapshot) => {
    const gameId = normalizeNumericId(snapshot.gameId);
    const key = packKey(snapshot.gameId, snapshot.packId);
    const row = rowsByPack.get(key) ?? {
      gameName: snapshot.gameName,
      gameId,
      packId: normalizeNumericId(snapshot.packId),
      ticketPrice: "",
      currentPackStatus: snapshot.currentPackStatus || UNKNOWN_PACK_STATUS,
      totalExpected: 0,
      ticketsByAudit: new Map<string, AuditTicketValue>(),
    };
    if (snapshot.currentPackStatus) {
      row.currentPackStatus = snapshot.currentPackStatus;
    }
    const ticketPrice = Number(snapshot.ticketPrice);
    if (Number.isFinite(ticketPrice)) {
      row.ticketPrice = ticketPrice;
    }
    row.totalExpected += snapshot.qtySold;
    const ticketValue = getAuditTicketValue(snapshot);
    if (ticketValue != null) {
      row.ticketsByAudit.set(snapshot.auditId, ticketValue);
    }
    rowsByPack.set(key, row);

    const auditGameKey = `${snapshot.auditId}:${gameId}`;
    if (!totalsByAuditGame.has(auditGameKey)) {
      totalsByAuditGame.set(auditGameKey, {
        gameId,
        reportedPos: snapshot.posQty,
        variance: snapshot.quantityVariance,
      });
    }
  });

  const totalsByGame = new Map<string, AuditedGameRangeTotals>();
  totalsByAuditGame.forEach((auditTotal) => {
    const rangeTotal = totalsByGame.get(auditTotal.gameId) ?? {
      gameId: auditTotal.gameId,
      reportedPos: 0,
      variance: 0,
    };
    rangeTotal.reportedPos += auditTotal.reportedPos;
    rangeTotal.variance += auditTotal.variance;
    totalsByGame.set(auditTotal.gameId, rangeTotal);
  });

  const sortedRows = Array.from(rowsByPack.values())
    .filter((row) => row.ticketsByAudit.size > 0)
    .sort(
      (left, right) =>
        compareNumericText(left.gameId, right.gameId) ||
        compareNumericText(left.packId, right.packId)
    );
  const header = [
    "Game Name",
    "Game ID",
    "Pack ID",
    "Ticket Price",
    "Current Pack Status",
    ...auditColumns.map((audit) => formatAuditDate(audit.auditDatetime)),
    "Total Expected",
    "POS Reported",
    "Variance",
  ];
  const userHeader = [
    "",
    "",
    "",
    "",
    "",
    ...auditColumns.map(getAuditUserLabel),
    "",
    "",
    "",
  ];
  const gamesWithSummary = new Set<string>();
  const rows = sortedRows.map((row) => {
    const isFirstPackForGame = !gamesWithSummary.has(row.gameId);
    const gameTotals = totalsByGame.get(row.gameId);
    gamesWithSummary.add(row.gameId);

    return [
      row.gameName,
      row.gameId,
      row.packId,
      row.ticketPrice,
      row.currentPackStatus,
      ...auditColumns.map((audit) => row.ticketsByAudit.get(audit.auditId) ?? ""),
      row.totalExpected,
      isFirstPackForGame ? gameTotals?.reportedPos ?? 0 : "",
      isFirstPackForGame ? gameTotals?.variance ?? 0 : "",
    ];
  });

  return `\ufeff${[header, userHeader, ...rows]
    .map((row) => row.map(csvCell).join(","))
    .join("\r\n")}\r\n`;
};

export const generateAuditedGamePacksCsv = async ({
  storeId,
  storeName,
  startDate,
  endDate,
  shifts,
}: AuditedGamePackCsvOptions): Promise<AuditedGamePackCsvFile> => {
  const uniqueShifts = Array.from(
    new Map<string, AuditShiftReference>(
      shifts
        .filter((shift) => Boolean(shift.auditId))
        .map(
          (shift): [string, AuditShiftReference] => [shift.auditId, shift]
        )
    ).values()
  ).sort((left, right) => {
    const leftTime = moment.parseZone(left.reportDatetime ?? "").valueOf();
    const rightTime = moment.parseZone(right.reportDatetime ?? "").valueOf();
    return leftTime - rightTime;
  });

  if (!uniqueShifts.length) {
    throw new Error("No audits were found in the selected date range.");
  }

  const [auditDetails, currentPackStatuses] = await Promise.all([
    loadAuditDetails(storeId, uniqueShifts),
    loadCurrentPackStatuses(storeId),
  ]);

  const snapshots: AuditPackSnapshot[] = auditDetails.flatMap(({ shift, detail }) =>
    detail.items.map((item: AuditDetailItem) => ({
      auditId: shift.auditId,
      gameId: item.gameId,
      gameName: item.gameName,
      packId: item.packId,
      ticketPrice: Number(item.costPerTicket),
      currentPackStatus:
        currentPackStatuses.get(packKey(item.gameId, item.packId)) ??
        UNKNOWN_PACK_STATUS,
      endingTicket: item.endingTicket,
      endingTicketDisplay: item.endingTicketDisplay,
      qtySold: Number(item.qtySold ?? 0),
      posQty: Number(item.posQty ?? 0),
      quantityVariance: Number(item.quantityVariance ?? 0),
      isManualSoldOut: Boolean(item.isManualSoldOut),
    }))
  );
  const auditColumns = auditDetails
    .filter(({ detail }) => detail.items.length > 0)
    .map(({ shift, detail }) => ({
      auditId: shift.auditId,
      auditDatetime: detail.auditDate || shift.reportDatetime || "",
      userName: shift.userName,
      operatorName: shift.operatorName,
    }));
  const content = buildAuditedGamePacksCsv(snapshots, auditColumns);
  const normalizedStoreName = (storeName || "store")
    .replace(/[^A-Za-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const safeStoreName = normalizedStoreName || "store";
  const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
  const formattedEndDate = moment(endDate).format("YYYY-MM-DD");

  return {
    blob: new Blob([content], { type: "text/csv;charset=utf-8" }),
    filename: `${safeStoreName}_scratcher_log_${formattedStartDate}_to_${formattedEndDate}.csv`,
  };
};
